import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../services/api';

interface FleetSummary {
  devices: number;
  devices_with_pending: number;
  pending_commands: number;
  storage_risk_devices: number;
  auth_risk_devices: number;
  connectivity_risk_devices: number;
  stale_devices: number;
}

interface DeviceHealth {
  device_id: string;
  user_name?: string | null;
  role?: string | null;
  app_version?: string | null;
  db_version?: number | null;
  pending_count: number;
  storage_state?: string | null;
  persistence_state?: string | null;
  auth_state?: string | null;
  connectivity_state?: string | null;
  last_error_code?: string | null;
  last_sync_at?: string | null;
  reported_at?: string | null;
}

export default function OfflineFleetHealthPanel() {
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [devices, setDevices] = useState<DeviceHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [summaryRes, devicesRes] = await Promise.all([
        apiClient.get('/offline/health/summary'),
        apiClient.get('/offline/health/devices'),
      ]);
      setSummary(summaryRes.data?.summary || null);
      setDevices(devicesRes.data?.devices || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Unable to load offline fleet health');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const metric = (label: string, value: number | undefined) => (
    <div className="rounded-lg border border-ink-200 dark:border-ink-800 p-3">
      <p className="text-[10px] uppercase tracking-[.12em] text-ink-500">{label}</p>
      <p className="text-xl font-semibold nums text-ink-900 dark:text-ink-50 mt-1">{Number(value || 0).toLocaleString()}</p>
    </div>
  );

  return (
    <section className="rounded-xl border border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900 p-5" aria-label="Offline fleet health">
      <div className="flex flex-wrap gap-3 items-start justify-between mb-4">
        <div><p className="text-[10px] uppercase tracking-[.14em] font-semibold text-ink-500">Offline operations</p><h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Field device synchronization health</h3><p className="text-xs text-ink-500 mt-1">Privacy-minimized device state. No GPS coordinates or farmer payloads are collected here.</p></div>
        <button type="button" className="btn-secondary text-xs" onClick={() => void refresh()} disabled={loading}>Refresh</button>
      </div>
      {error && <div className="rounded-lg border border-danger-300 bg-danger-50 dark:bg-danger-500/10 px-3 py-2 text-xs text-danger-700 dark:text-danger-300 mb-4">{error}</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mb-4">
        {metric('Devices', summary?.devices)}
        {metric('With pending', summary?.devices_with_pending)}
        {metric('Pending commands', summary?.pending_commands)}
        {metric('Storage risk', summary?.storage_risk_devices)}
        {metric('Auth risk', summary?.auth_risk_devices)}
        {metric('Connectivity risk', summary?.connectivity_risk_devices)}
        {metric('Stale >24h', summary?.stale_devices)}
      </div>
      <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-ink-50 dark:bg-ink-950"><tr><th className="text-left p-2">Device</th><th className="text-left p-2">User</th><th className="text-left p-2">App / DB</th><th className="text-left p-2">Connectivity</th><th className="text-left p-2">Storage</th><th className="text-right p-2">Pending</th><th className="text-left p-2">Last sync</th><th className="text-left p-2">Last report</th><th className="text-left p-2">Error</th></tr></thead>
          <tbody>
            {devices.length === 0 && !loading ? <tr><td colSpan={9} className="p-5 text-center text-ink-500">No client health reports received yet.</td></tr> : devices.slice(0, 100).map(device => <tr key={device.device_id} className="border-t border-ink-200 dark:border-ink-800"><td className="p-2 font-mono">{device.device_id.slice(0,12)}…</td><td className="p-2">{device.user_name || 'Unknown'}{device.role ? ` · ${device.role}` : ''}</td><td className="p-2">{device.app_version || '?'} / DB {device.db_version ?? '?'}</td><td className="p-2">{device.connectivity_state || 'UNKNOWN'}</td><td className="p-2">{device.storage_state || 'UNKNOWN'} · {device.persistence_state || 'UNKNOWN'}</td><td className="p-2 text-right font-semibold nums">{device.pending_count || 0}</td><td className="p-2">{device.last_sync_at ? new Date(device.last_sync_at).toLocaleString() : 'Never'}</td><td className="p-2">{device.reported_at ? new Date(device.reported_at).toLocaleString() : 'Unknown'}</td><td className="p-2">{device.last_error_code || '—'}</td></tr>)}
          </tbody>
        </table>
      </div>
    </section>
  );
}
